import { useEffect } from "react";
import { Constants } from "../constants/constants";

/**
 * Seo — per-page document metadata (title, description, canonical,
 * Open Graph, Twitter card, JSON-LD structured data).
 *
 * Why imperative upserts instead of React 19's native metadata hoisting or
 * react-helmet-async?
 * - React 19 hoists <title>/<meta> but only dedupes tags React rendered
 *   itself; the static tags in public/index.html would get duplicated.
 * - react-helmet-async does not declare React 19 peer support.
 * So we update the existing shell tags in place (create only when missing),
 * which guarantees exactly one of each tag at all times.
 */

interface SeoProps {
  /** Full page title, e.g. "Acme Clinic — Carely IT". */
  title: string;
  description?: string;
  /** Path relative to the site root ("/about"); resolved against SITE_URL. */
  canonicalPath?: string;
  /** Absolute URL of the share image (og:image / twitter:image). */
  image?: string | null;
  /** Open Graph type. */
  type?: "website" | "article" | "profile";
  /** true → robots "noindex, nofollow" (404 / private screens). */
  noIndex?: boolean;
  /** schema.org JSON-LD object (or list) to embed. */
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

type MetaKey = { name: string } | { property: string };

const metaSelector = (key: MetaKey): string =>
  "name" in key
    ? `meta[name="${key.name}"]`
    : `meta[property="${key.property}"]`;

/** Updates an existing meta tag or appends a new one; null content removes it. */
const upsertMeta = (key: MetaKey, content: string | null) => {
  const selector = metaSelector(key);
  const existing = document.head.querySelector<HTMLMetaElement>(selector);

  if (content == null) {
    existing?.remove();
    return;
  }

  if (existing) {
    existing.setAttribute("content", content);
    return;
  }

  const el = document.createElement("meta");
  if ("name" in key) el.setAttribute("name", key.name);
  else el.setAttribute("property", key.property);
  el.setAttribute("content", content);
  document.head.appendChild(el);
};

const upsertCanonical = (url: string) => {
  let link = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
};

const JSON_LD_ID = "seo-jsonld";

const removeJsonLd = () => {
  document.getElementById(JSON_LD_ID)?.remove();
};

const upsertJsonLd = (
  data: Record<string, unknown> | Array<Record<string, unknown>>,
) => {
  removeJsonLd();
  const script = document.createElement("script");
  script.id = JSON_LD_ID;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

const Seo = ({
  title,
  description,
  canonicalPath,
  image,
  type = "website",
  noIndex = false,
  jsonLd,
}: SeoProps) => {
  useEffect(() => {
    const pageUrl = canonicalPath
      ? `${Constants.SITE_URL}${canonicalPath}`
      : undefined;

    document.title = title;

    upsertMeta(
      { name: "robots" },
      noIndex ? "noindex, nofollow" : "index, follow",
    );
    upsertMeta({ name: "description" }, description ?? null);

    upsertMeta({ property: "og:title" }, title);
    upsertMeta({ property: "og:description" }, description ?? null);
    upsertMeta({ property: "og:type" }, type);
    if (pageUrl) upsertMeta({ property: "og:url" }, pageUrl);
    upsertMeta({ property: "og:image" }, image ?? null);

    upsertMeta(
      { name: "twitter:card" },
      image ? "summary_large_image" : "summary",
    );
    upsertMeta({ name: "twitter:title" }, title);
    upsertMeta({ name: "twitter:description" }, description ?? null);
    upsertMeta({ name: "twitter:image" }, image ?? null);

    if (pageUrl) upsertCanonical(pageUrl);

    if (jsonLd) upsertJsonLd(jsonLd);
    else removeJsonLd();

    return () => {
      // Remove only tags that are NOT part of the static shell, so a page
      // without Seo never inherits stale structured data or share images.
      removeJsonLd();
      document.head.querySelector('meta[name="twitter:image"]')?.remove();
    };
  }, [title, description, canonicalPath, image, type, noIndex, jsonLd]);

  return null;
};

export default Seo;
